import { EntityTypes } from '@interfaces/user';
import { Link as MuiLink } from '@ovComponents/1-primative/link/link';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { currencySymbol, isCurrencyCode } from '@utils/currency';
import { generateClientNameString } from '@utils/generateClientNameString';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Skeleton, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';

export type ExposureLevel = 'HOUSEHOLD' | 'ENTITY' | 'ACCOUNT';

export type ProductAumExposureRow = {
  rank: number;
  marketValueCents: number;
  household?: {
    id: string;
    name?: string;
  };
  user?: {
    id: string;
    type?: EntityTypes;
    firstName?: string;
    lastName?: string;
    entityName?: string;
  };
  account?: {
    id: string;
    type?: string;
    custodianAccountNumber?: string;
    user?: {
      id: string;
      type?: EntityTypes;
      firstName?: string;
      lastName?: string;
      entityName?: string;
    };
  };
};

const getAccountLabel = (
  account: NonNullable<ProductAumExposureRow['account']>,
) => {
  const parts = [account.type, account.custodianAccountNumber].filter(Boolean);
  return parts.join(' #') || account.id;
};

const getNameForRow = (
  row: ProductAumExposureRow,
  exposureLevel: ExposureLevel,
): string => {
  if (exposureLevel === 'HOUSEHOLD' && row.household?.name) {
    return row.household.name;
  }
  if (exposureLevel === 'ENTITY' && row.user) {
    return generateClientNameString(row.user);
  }
  if (exposureLevel === 'ACCOUNT' && row.account) {
    const accountLabel = getAccountLabel(row.account);
    const entityName = row.account.user
      ? generateClientNameString(row.account.user)
      : '';
    return entityName ? `${entityName} - ${accountLabel}` : accountLabel;
  }
  return '';
};

const getEntityPath = (user: { id: string; type?: EntityTypes }): string =>
  user.type === EntityTypes.INDIVIDUAL
    ? `/clients/${user.id}`
    : `/nonIndividualClients/${user.id}`;

const getRowPath = (
  row: ProductAumExposureRow,
  exposureLevel: ExposureLevel,
): string | undefined => {
  if (exposureLevel === 'HOUSEHOLD' && row.household?.id) {
    return `/households/${row.household.id}`;
  }
  if (exposureLevel === 'ENTITY' && row.user?.id) {
    return getEntityPath(row.user);
  }
  if (exposureLevel === 'ACCOUNT' && row.account?.id) {
    const accountUser = row.account.user;
    if (accountUser?.id) {
      return `${getEntityPath(accountUser)}/account/${row.account.id}`;
    }
    return `/accounts/${row.account.id}`;
  }
  return undefined;
};

const nameColumnKey: Record<ExposureLevel, string> = {
  HOUSEHOLD: 'householdName',
  ENTITY: 'entityName',
  ACCOUNT: 'accountName',
};

export const ProductAumExposureTable = ({
  exposures,
  exposureLevel,
  loading,
  hasPreviousData,
  baseCurrency,
}: {
  exposures: ProductAumExposureRow[];
  exposureLevel: ExposureLevel;
  loading: boolean;
  hasPreviousData: boolean;
  baseCurrency: string;
}) => {
  const { t } = useTranslation(['homePageConfiguration']);
  const moneySymbol = isCurrencyCode(baseCurrency)
    ? (currencySymbol[baseCurrency] ?? '$')
    : '$';

  if (!loading && exposures.length === 0) {
    return (
      <Typography variant='bodyMedium' sx={{ px: 2, py: 3, fontWeight: 500 }}>
        {t('homePageConfiguration:productAumExposure.noResults')}
      </Typography>
    );
  }

  return (
    <Table aria-label='product-aum-exposure-table' sx={{ minWidth: 650 }}>
      <TableBody>
        <TableRow>
          <TableHeadCell isFirst>
            {t('homePageConfiguration:productAumExposure.columns.rank')}
          </TableHeadCell>
          <TableHeadCell>
            {t(
              `homePageConfiguration:productAumExposure.columns.${nameColumnKey[exposureLevel]}`,
            )}
          </TableHeadCell>
          <TableHeadCell>
            {t('homePageConfiguration:productAumExposure.columns.marketValue')}
          </TableHeadCell>
        </TableRow>
        {loading &&
          !hasPreviousData &&
          [...Array(5)].map((_row, index) => (
            <TableRow key={`skeleton-${index}`}>
              {[0, 1, 2].map((cellIndex) => (
                <TableCell key={cellIndex} dense isFirst={cellIndex === 0}>
                  <Skeleton width='100%' height='16px' />
                </TableCell>
              ))}
            </TableRow>
          ))}
        {exposures.map((row) => {
          const name = getNameForRow(row, exposureLevel);
          const path = getRowPath(row, exposureLevel);

          return (
            <TableRow key={`${exposureLevel}-${row.rank}`}>
              <TableCell dense isFirst>
                {row.rank}
              </TableCell>
              <TableCell dense>
                {path ? (
                  <MuiLink component={Link} to={path}>
                    {name}
                  </MuiLink>
                ) : (
                  name
                )}
              </TableCell>
              <TableCell dense>
                {formatMoneyValue(
                  row.marketValueCents,
                  moneySymbol,
                  2,
                  true,
                  true,
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
